/**
 * EditorClueCell — WYSIWYG editable clue cell in the front panel.
 *
 * Default state: SVG preview showing exactly how the text will appear in-game,
 * including multi-line breaks from display hints and auto-fitting.
 *
 * Click to edit: transparent input overlay appears for typing.
 * Blur: returns to SVG preview.
 *
 * Two-layer rendering:
 *   - Sharp-corner outer rect (208×78): cream/charcoal alternating
 *   - Rounded inner overlay (196×66): teal/orange for hidden cells
 * Hidden clue text uses slightly brighter tan (#c8b8a0).
 *
 * Player View mode (showButton=true): renders HintButton/GuessButton SVG
 * instead of text, showing what the player sees at game start.
 */

import React, { useRef, useMemo, useState, useCallback } from 'react';
import { fitClueText } from '../../utils/textFitting';
import type { DisplayHints } from '../../types';
import HintButton from '../../svg/HintButton';
import GuessButton from '../../svg/GuessButton';
import styles from './EditorClueCell.module.css';

interface EditorClueCellProps {
  text: string;
  rowIndex: number;
  pairIndex: number;
  isHidden: boolean;
  displayHint?: DisplayHints;
  onChange: (text: string) => void;
  onFocus?: () => void;
  /** Animation class to apply to the cover overlay */
  slideAnim?: 'slideUp' | 'slideDown' | null;
  /** When true, render the game's HintButton/GuessButton SVG instead of text */
  showButton?: boolean;
}

// SVG dimensions matching ClueLozenge
const SVG_W = 196;
const SVG_H = 66;

const EditorClueCell: React.FC<EditorClueCellProps> = ({
  text,
  rowIndex,
  pairIndex,
  isHidden,
  displayHint,
  onChange,
  onFocus,
  slideAnim,
  showButton = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);

  const fit = useMemo(
    () => fitClueText(text || 'X', undefined, displayHint),
    [text, displayHint]
  );
  const isEmpty = !text;
  const showFitInfo = !isEmpty;

  // Background class (sharp-corner outer rect)
  const bgClass = rowIndex % 2 === 0 ? styles.bgEven : styles.bgOdd;

  // Cover overlay class (only shown when hidden and not in button mode)
  const coverClass = pairIndex === 2 ? styles.coverGuess : styles.coverHint;

  // Text color for SVG preview — slightly brighter on colored overlays
  const textColor = isHidden
    ? '#c8b8a0'
    : rowIndex % 2 === 0 ? '#262626' : '#f7e5c5';

  // Text class for the edit input
  const textClass = isHidden
    ? styles.textDimmed
    : rowIndex % 2 === 0 ? styles.textDark : styles.textCream;

  // Animation class for the overlay
  const animClass = slideAnim ? styles[slideAnim] : '';

  // SVG text rendering — matches ClueLozenge exactly
  const effectiveFontSize = fit.fontSize;
  const lines = isEmpty ? [] : fit.lines;
  const lineHeight = effectiveFontSize * 1.1;
  const totalTextHeight = lines.length * lineHeight;
  const startY = (SVG_H - totalTextHeight) / 2 + lineHeight / 2;

  const handleClick = useCallback(() => {
    if (showButton) return; // Don't allow editing in player view
    setEditing(true);
    // Focus the input after React renders it
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [showButton]);

  const handleBlur = useCallback(() => {
    setEditing(false);
    // Auto-trim leading/trailing spaces when editing finishes
    const trimmed = text.trim();
    if (trimmed !== text) {
      onChange(trimmed);
    }
  }, [text, onChange]);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Player view: show actual button SVG for hidden cells
  if (showButton && isHidden) {
    const ButtonComponent = pairIndex === 2 ? GuessButton : HintButton;
    return (
      <div className={`${styles.cellOuter} ${bgClass}`}>
        <div className={styles.previewWrap}>
          <ButtonComponent
            disabled
            style={{ width: SVG_W, height: SVG_H, opacity: 1 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.cellOuter} ${bgClass} ${textClass}`}
      onClick={!editing ? handleClick : undefined}
    >
      {/* Colored cover overlay for hidden cells */}
      {isHidden && (
        <div className={`${styles.coverOverlay} ${coverClass} ${animClass}`} />
      )}

      {/* Layer: SVG preview (visible when not editing) */}
      {!editing && (
        <div className={styles.previewWrap}>
          {isEmpty ? (
            <span className={styles.placeholder}>
              {isHidden ? 'hidden' : 'visible'}
            </span>
          ) : (
            <svg
              width={SVG_W}
              height={SVG_H}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              className={styles.previewSvg}
            >
              {lines.length === 1 && lines[0] && (
                <text
                  x="50%"
                  y="50%"
                  dy=".1em"
                  fill={textColor}
                  fontFamily="Inter, sans-serif"
                  fontSize={effectiveFontSize}
                  fontWeight="700"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  pointerEvents="none"
                >
                  {lines[0]}
                </text>
              )}
              {lines.length > 1 && (
                <text
                  fill={textColor}
                  fontFamily="Inter, sans-serif"
                  fontSize={effectiveFontSize}
                  fontWeight="700"
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {lines.map((line, i) => (
                    <tspan
                      key={i}
                      x="50%"
                      y={startY + i * lineHeight}
                      dominantBaseline="middle"
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              )}
            </svg>
          )}
        </div>
      )}

      {/* Layer: Edit input (visible when editing) */}
      {editing && (
        <div className={styles.inputWrap}>
          <input
            ref={inputRef}
            className={styles.input}
            style={{ fontSize: 20 }}
            value={text}
            placeholder={isHidden ? 'hidden' : 'visible'}
            onChange={e => onChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            maxLength={80}
          />
        </div>
      )}

      {showFitInfo && fit.needsManualFix && (
        <div className={styles.fitWarning} title="Text too long — needs manual line break or shorter text" />
      )}
      {showFitInfo && fit.isSmaller && !fit.needsManualFix && (
        <span className={styles.sizeIndicator}>{fit.fontSize}px</span>
      )}
    </div>
  );
};

export default React.memo(EditorClueCell);
